import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchOrderByGuId,
  fetchOrderBillingAddressByGuid,
  fetchOrderItemsByGuId,
} from "../../../services/orderService";
import { Loading } from "../../Common/OtherElements/Loading";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import allImages from "../../../assets/images-import";

const InvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [billingAddress, setBillingAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageAccessDetails, setPageAccessDetails] = useState([]);

  const PageLevelAccessurl = "order";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    if (id && pageAccessDetails.viewAccess) {
      fetchOrderDetails();
    }
  }, [id, pageAccessDetails]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);

      const [orderData, itemsData, billingData] = await Promise.allSettled([
        fetchOrderByGuId(id),
        fetchOrderItemsByGuId(id),
        fetchOrderBillingAddressByGuid(id),
      ]);

      if (orderData.status === "fulfilled" && orderData.value?.isSuccess) {
        setOrder(orderData.value.result || orderData.value);
      }
      if (itemsData.status === "fulfilled" && itemsData.value?.isSuccess) {
        setOrderItems(itemsData.value.result || itemsData.value);
      }

      if (billingData.status === "fulfilled" && billingData.value?.isSuccess) {
        setBillingAddress(billingData.value.result || billingData.value);
      }
    } catch (error) {
      console.error("Failed to fetch order details:", error);
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${day}-${month}-${year} ${time}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "badge-soft-success";
      case "dispatched":
        return "badge-soft-info";
      case "confirmed":
        return "badge-soft-primary";
      case "in process":
        return "badge-soft-warning";
      case "cancelled":
        return "badge-soft-danger";
      case "paid":
        return "badge-soft-success";
      case "pending":
      case "initiated":
        return "badge-soft-warning";
      case "failed":
        return "badge-soft-danger";
      default:
        return "badge-soft-secondary";
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!pageAccessDetails.viewAccess) {
    return null;
  }

  if (!order) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center">
              <p>Order not found</p>
              <button className="btn btn-primary" onClick={() => navigate(-1)}>
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .progress-step-arrow {
            display: flex;
            background: #f8f9fa;
            border-radius: 0.375rem;
            overflow: hidden;
          }
          .progress-step-arrow .progress-bar {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
            font-weight: 500;
            border-right: 1px solid #dee2e6;
          }
          .progress-step-arrow .progress-bar:last-child {
            border-right: none;
          }
          .progress-step-arrow .progress-bar.initiated {
            background-color: #0d6efd;
            color: white;
          }
          .progress-step-arrow .progress-bar.confirmed {
            background-color: ${
              order.orderStatus === "Confirmed" ||
              order.orderStatus === "Dispatched" ||
              order.orderStatus === "Delivered"
                ? "#0d6efd"
                : "#e9ecef"
            };
            color: ${
              order.orderStatus === "Confirmed" ||
              order.orderStatus === "Dispatched" ||
              order.orderStatus === "Delivered"
                ? "white"
                : "#6c757d"
            };
          }
          .progress-step-arrow .progress-bar.pendingProcess:nth-child(3) {
            background-color: ${
              order.orderStatus === "Dispatched" ||
              order.orderStatus === "Delivered"
                ? "#0d6efd"
                : "#e9ecef"
            };
            color: ${
              order.orderStatus === "Dispatched" ||
              order.orderStatus === "Delivered"
                ? "white"
                : "#6c757d"
            };
          }
          .progress-step-arrow .progress-bar.pendingProcess:nth-child(4) {
            background-color: ${
              order.orderStatus === "Delivered" ? "#0d6efd" : "#e9ecef"
            };
            color: ${order.orderStatus === "Delivered" ? "white" : "#6c757d"};
          }
          .table>:not(caption)>*>* {
            padding: .75rem 0.5rem !important;
          }
        `}
      </style>
      <ComponentHeader title="Order Invoice" />

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              <div className="row gy-4">
                <div className="col-lg-12">
                  <div className="card-header border-bottom-dashed p-4">
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <img
                          src={allImages.logoDark}
                          className="card-logo card-logo-dark"
                          alt="logo dark"
                          height="70"
                        />
                      </div>
                      <div className="flex-shrink-0 mt-sm-0 mt-3">
                        <p
                          className="text-muted mb-2 fs-14 "
                          id="address-details"
                        >
                          <strong>NCart Pvt Ltd.</strong>
                          <br />
                        </p>
                        <h6 className="address mb-2">
                          <span className="text-muted fw-normal">
                            Address:{" "}
                          </span>
                          <span className="mb-1 d-inline-block">
                            Ground Floor, No 110,
                          </span>
                          <span className="mb-1 d-block">
                            18th Main Indiranagar
                          </span>
                          <span className="mb-1 d-block">
                            100 Feet Road,HAL 2nd Stage,
                          </span>
                          <span className="mb-1 d-block">
                            Bengaluru 560008,Karnataka
                          </span>
                        </h6>
                        <h6>
                          <span className="text-muted fw-normal">
                            Zip-code:
                          </span>
                          <span id="zipcode"> 560008</span>
                        </h6>
                        <h6>
                          <span className="text-muted fw-normal">GST No:</span>
                          <span id="legal-register-no"> 29AASFB7372A2Z7</span>
                        </h6>
                        <h6>
                          <span className="text-muted fw-normal">Email:</span>
                          <span id="email"> support@ncart.com</span>
                        </h6>
                        <h6 className="mb-0">
                          <span className="text-muted fw-normal">
                            Contact No:{" "}
                          </span>
                          <span id="contact-no">+91 97421 52763</span>
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-12 bg-soft-dark">
                  <div className="card-header border-bottom-dashed p-4">
                    <div className="row">
                      <div className="col-lg-6 col-6">
                        <p className="mb-0 fs-14 text-black">
                          <strong>Invoice No</strong>: {order.invoiceNo}
                        </p>
                      </div>
                      <div className="col-lg-6 col-6"></div>
                    </div>
                  </div>
                </div>
                {/* Order Summary */}
                <div className="col-lg-12">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      <div className="col-lg-3 col-6">
                        <p className="text-muted mb-2 text-uppercase fw-semibold">
                          Order No
                        </p>
                        <h5 className="fs-14 mb-0">#{order.orderId}</h5>
                      </div>
                      <div className="col-lg-3 col-6">
                        <p className="text-muted mb-2 text-uppercase fw-semibold">
                          Date
                        </p>
                        <h5 className="fs-14 mb-0">
                          {formatDate(order.orderedOn)}
                        </h5>
                      </div>
                      <div className="col-lg-3 col-6">
                        <p className="text-muted mb-2 text-uppercase fw-semibold">
                          Order Status
                        </p>
                        <span
                          className={`badge ${getStatusBadgeClass(
                            order.orderStatus
                          )} badge-border`}
                        >
                          {order.orderStatus}
                        </span>
                      </div>
                      <div className="col-lg-3 col-6">
                        <p className="text-muted mb-2 text-uppercase fw-semibold">
                          Payment Status
                        </p>
                        <span
                          className={`badge ${getStatusBadgeClass(
                            order.paymentStatus
                          )} badge-border`}
                        >
                          {order.paymentStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer and Address Info */}
                <div className="col-lg-12">
                  <div className="card-body p-4 border-top border-top-dashed">
                    <div className="row g-3">
                      <div className="col-6">
                        <h6 className="text-muted text-uppercase fw-semibold mb-3">
                          Customer & Billing Address
                        </h6>
                        <p className="text-muted mb-2">
                          <strong>Name: </strong>
                          <span>{order.userName}</span>
                        </p>
                        <p className="text-muted mb-2">
                          <strong>Email: </strong>
                          <a
                            className="text-muted"
                            href={`mailto:${order.emailId}`}
                          >
                            {order.emailId}
                          </a>
                        </p>
                        <p className="text-muted mb-2">
                          <strong>Contact No: </strong>
                          <a
                            className="text-muted"
                            href={`tel:${order.mobileNo}`}
                          >
                            {order.mobileNo}
                          </a>
                        </p>

                        {billingAddress && (
                          <>
                            <p className="text-muted mb-2">
                              <strong>Address: </strong>
                              <span>
                                {billingAddress.buildingName},{" "}
                                {billingAddress.street}, {billingAddress.city},{" "}
                                {billingAddress.state}, {billingAddress.country}{" "}
                                - {billingAddress.pinCode}
                              </span>
                            </p>
                            {billingAddress.companyName && (
                              <p className="text-muted mb-2">
                                <strong>Company: </strong>
                                <span>{billingAddress.companyName}</span>
                              </p>
                            )}
                            {billingAddress.gstNo && (
                              <p className="text-muted mb-2">
                                <strong>GSTIN: </strong>
                                <span>{billingAddress.gstNo}</span>
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items Table */}
                <div className="col-lg-12">
                  <div className="card-body p-4">
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered text-left table-nowrap align-middle mb-0">
                        <thead>
                          <tr className="table-active">
                            <th scope="col" style={{ width: "50px" }}>
                              #
                            </th>
                            <th scope="col">Item</th>
                            <th scope="col">Product Size</th>
                            <th scope="col">Price</th>
                            <th scope="col">Quantity</th>
                            <th scope="col">Tax Percent</th>
                            <th scope="col">Tax Amount</th>
                            <th scope="col">Sub Total Without Tax</th>
                            <th scope="col">Total Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(orderItems) &&
                            orderItems.map((item, index) => (
                              <tr key={item.id || index}>
                                <td>{index + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div>
                                      <h6 className="mb-0">
                                        {item.productName}
                                      </h6>
                                    </div>
                                  </div>
                                </td>
                                <td>{item.productSize}</td>
                                <td>{formatCurrency(item.discountPrice)}</td>
                                <td>{item.quantity}</td>
                                <td>{item.taxPercent}%</td>
                                <td>
                                  {formatCurrency(
                                    ((item.discountPrice * item.taxPercent) /
                                      (100 + item.taxPercent)) *
                                      item.quantity
                                  )}
                                </td>
                                <td>
                                  {formatCurrency(
                                    item.discountPrice * item.quantity -
                                      ((item.discountPrice * item.taxPercent) /
                                        (100 + item.taxPercent)) *
                                        item.quantity
                                  )}
                                </td>
                                <td>
                                  <strong>
                                    {formatCurrency(
                                      item.discountPrice * item.quantity
                                    )}
                                  </strong>
                                </td>
                              </tr>
                            ))}

                          {/* Summary rows */}
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>SubTotal Without Tax</strong>
                            </td>
                            <td>
                              <strong>
                                {formatCurrency(order.subTotalWithoutTax)}
                              </strong>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>Tax</strong>
                            </td>
                            <td>
                              <strong>{formatCurrency(order.tax)}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>Shipping Charges</strong>
                            </td>
                            <td>
                              <strong>
                                {formatCurrency(order.shippingPrice)}
                              </strong>
                            </td>
                          </tr>
                          {order.discount > 0 && (
                            <tr>
                              <td colSpan="8" className="text-end">
                                <strong>Discount</strong>
                              </td>
                              <td>
                                <strong className="text-success">
                                  -{formatCurrency(order.discount)}
                                </strong>
                              </td>
                            </tr>
                          )}
                          {order.couponDiscount > 0 && (
                            <tr>
                              <td colSpan="8" className="text-end">
                                <strong>
                                  Coupon Discount{" "}
                                  {order.couponCode && `(${order.couponCode})`}
                                </strong>
                              </td>
                              <td>
                                <strong className="text-success">
                                  -{formatCurrency(order.couponDiscount)}
                                </strong>
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>Total Price</strong>
                            </td>
                            <td>
                              <strong className="text-primary fs-16">
                                {formatCurrency(order.totalPrice)}
                              </strong>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>Paid Amount</strong>
                            </td>
                            <td>
                              <strong className="text-success">
                                {formatCurrency(order.paidAmount)}
                              </strong>
                            </td>
                          </tr>
                          {order.balanceAmount > 0 && (
                            <tr>
                              <td colSpan="8" className="text-end">
                                <strong>Balance Amount</strong>
                              </td>
                              <td>
                                <strong className="text-danger">
                                  {formatCurrency(order.balanceAmount)}
                                </strong>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceDetails;
